export interface IntegrationSection {
  heading: string;
  content: string; // can contain HTML or we can just use strings and render it. Let's stick to simple strings or arrays of strings.
  list?: string[];
}

export interface IntegrationFaq {
  q: string;
  a: string;
}

export interface Integration {
  name: string;
  slug: string;
  logo: string;
  website: string;
  seoTitle: string;
  seoDescription: string;
  shortDescription: string;
  intro: string;
  sections: IntegrationSection[];
  faqs: IntegrationFaq[];
  relatedSlugs: string[];
}

const integrationData: Integration[] = [
  {
    name: "Amazon Web Services",
    slug: "aws",
    logo: "/images/relevant-bi-integrations/aws.png",
    website: "https://aws.amazon.com/security/",
    seoTitle: "AWS Integration: SFTP, SMTP & Custom Connectors",
    seoDescription: "Explore Relevant BI integration with Amazon Web Services for SFTP, SMTP and custom connectors, with technical guidance on layered security and data access.",
    shortDescription: "An AWS-based integration layer for external file transfers, email delivery, and custom connectors—bringing authorized data into a clearer reporting experience.",
    intro: "Integrating Relevant BI with Amazon Web Services (AWS) provides an architectural foundation for external connections that do not fit a standard reporting connector. SFTP transfers, SMTP delivery, and customer-specific connectors have different trust boundaries. The objective is to control each boundary independently, then bring authorized information into the same intelligence workspace as website, search, advertising, and vehicle activity. The architecture below describes available AWS controls and recommended implementation practices, not an attestation that every control is enabled in every Relevant BI deployment.",
    sections: [
      {
        heading: "Defense in depth, not a single security switch",
        content: "A security-focused integration layer separates transport encryption, identity, authorization, storage protection, and monitoring. AWS supplies building blocks for these layers; application owners must configure them and maintain the surrounding processes. A connector should receive only the permissions required for its specific source and destination, rather than broad access to an entire account or reporting environment.",
        list: [
          "Scope IAM permissions to specific resources and required actions",
          "Prefer temporary workload credentials where supported",
          "Separate connector identities and customer data boundaries",
          "Review permissions, retention, and access revocation over time"
        ]
      },
      {
        heading: "SFTP: encrypted file transport with explicit trust",
        content: "SFTP runs over SSH, rather than transmitting files through unencrypted FTP. AWS Transfer Family supports managed SFTP endpoints and connectors for file exchange. A reviewed design verifies the remote server's host key, scopes user access to the appropriate destination, and manages authentication keys through a defined lifecycle. Transport encryption does not by itself protect a file after it reaches storage: storage encryption, key permissions, retention, and application authorization require separate decisions.",
        list: [
          "Validate file type, size, schema, and source before importing",
          "Quarantine malformed or unexpected payloads instead of silently loading them",
          "Consider S3 encryption and AWS KMS key policies for stored files",
          "Record transfer outcomes and make retry behavior explicit"
        ]
      },
      {
        heading: "SMTP: protect submission and understand delivery limits",
        content: "For email submission, Amazon SES provides an SMTP interface with TLS support and region-specific SMTP credentials. A security-focused configuration requires an encrypted connection and stores credentials outside source code and browser bundles. TLS on the submission connection is not the same as end-to-end message encryption. Delivery to a recipient's mail server has its own TLS policy and compatibility considerations; sensitive data should not be placed in an email merely because SMTP submission uses TLS.",
        list: [
          "Use SES SMTP credentials appropriate to the selected AWS Region",
          "Require TLS for submission and review outbound TLS requirements",
          "Configure SPF, DKIM, and DMARC for domain authentication—not message encryption",
          "Monitor delivery failures and avoid logging sensitive message content"
        ]
      },
      {
        heading: "Custom customer connectors: narrow access, validated inputs",
        content: "A custom connector may need an API credential, scheduled file import, or customer-specific transformation. The security review should define exactly what it can read, where it can write, and how failures are contained. AWS Secrets Manager can store connector secrets; rotation requires support and configuration for the particular credential. IAM roles, network restrictions, request validation, rate limits, and controlled retries can reduce exposure when implemented correctly. None replaces validation of the external system itself.",
        list: [
          "Validate schemas and reject unexpected fields before downstream processing",
          "Use idempotent imports to prevent duplicate records during retries",
          "Restrict network access where the selected services support it",
          "Keep credentials and sensitive payloads out of diagnostic logs"
        ]
      },
      {
        heading: "From external data to understandable cross-source reports",
        content: "The value is not simply moving a file or sending an email. An authorized external dataset can add context to the sources already enabled in Relevant BI. For example, a scheduled customer-provided export can be reviewed alongside GA4 engagement and Google Ads spend once its fields, dates, and identifiers are mapped. SMTP supports delivery or notifications; it is not itself evidence of a lead or a sale. Retaining source labels and refresh timestamps helps teams distinguish current observations from delayed imports and avoid treating correlation as complete attribution."
      },
      {
        heading: "Auditability and the shared responsibility boundary",
        content: "AWS CloudTrail can record supported API activity, while service logs and CloudWatch can support operational visibility where configured. Coverage, retention, access to logs, and alert routing must be deliberately chosen; an audit trail is only useful if the relevant events are captured and reviewed. AWS secures its underlying cloud infrastructure, while customers remain responsible for their data, permissions, application code, and service configuration. AWS certifications do not automatically certify Relevant BI or an individual connector."
      }
    ],
    faqs: [
      {
        q: "How does integrating Relevant BI with AWS help secure external connections?",
        a: "AWS offers separate controls for encrypted transport, identity, access policy, secret storage, and monitoring. The integration design can combine those controls around SFTP, SMTP, and custom connectors. Actual protections depend on the services selected, their configuration, and the permissions granted; AWS hosting alone is not a security guarantee."
      },
      {
        q: "Does SFTP also guarantee encryption at rest?",
        a: "No. SFTP encrypts the SSH transport. Files stored after transfer require their own storage encryption and access policies. An AWS-based design can use S3 encryption and, where selected, customer-managed AWS KMS keys with reviewed permissions."
      },
      {
        q: "Is SMTP over TLS end-to-end encrypted email?",
        a: "No. TLS protects a particular transport connection. SES submission and delivery to a recipient mail server are separate stages. Recipient compatibility and the configured delivery policy matter, and the message is not automatically encrypted end to end."
      },
      {
        q: "Can each custom connector have separate credentials and permissions?",
        a: "Yes, AWS supports architectures with distinct identities, resource-scoped IAM policies, and managed secret storage. Separation and rotation must be implemented for each connector, including support from the external provider. Confirm the specific arrangement during onboarding."
      },
      {
        q: "Which AWS security controls are enabled for my dealership?",
        a: "Request a configuration review covering the chosen AWS services, transfer protocols, encryption, identity boundaries, logging, and retention. This page describes architectural capabilities and recommended practices; it is not a customer-specific security assessment or a claim of certification."
      }
    ],
    relatedSlugs: ["google-cloud", "fortellis", "ga4"]
  },
  {
    name: "Auto/Mate",
    slug: "automate",
    logo: "/images/relevant-bi-integrations/automate.png",
    website: "https://automate.com/",
    seoTitle: "Relevant BI Integration with Auto/Mate",
    seoDescription: "Learn about integrating Relevant BI with Auto/Mate. Connect your dealership management software data with marketing intelligence to see a clearer picture.",
    shortDescription: "Connect your Auto/Mate dealership management data to the Relevant BI hub to bring operational context alongside marketing performance.",
    intro: "Relevant BI integration with Auto/Mate brings essential dealership management context into your marketing intelligence hub. Auto/Mate is a comprehensive dealership management software system covering dealership operations, fixed and service operations, parts, F&I, and reporting. By integrating Relevant BI with Auto/Mate, dealership leaders can view these operational signals alongside website, search, and advertising data.",
    sections: [
      {
        heading: "Connecting Operations and Marketing",
        content: "While Auto/Mate effectively manages dealership operations and internal reporting, it does not independently optimize external marketing. However, when you integrate Auto/Mate data into the Relevant BI dashboard, you gain the ability to analyze marketing efforts with operational context in mind. This allows your team to understand how advertising initiatives correspond to the broader health of your dealership."
      },
      {
        heading: "A Clearer Picture for Decision Makers",
        content: "By centralizing authorized data sources, the Relevant BI integration with Auto/Mate helps executive conversations start with a shared view of the facts. Depending on configured access, leaders can compare available dealership operating context with external marketplace attention, website activity, and advertising spend.",
        list: [
          "View operational context alongside marketing traffic",
          "Inform marketing strategy with dealership management realities",
          "Keep executive discussions focused on a unified set of data"
        ]
      }
    ],
    faqs: [
      {
        q: "Does integrating Relevant BI with Auto/Mate automatically optimize our external marketing?",
        a: "No. Auto/Mate provides essential dealership management and operational data. Relevant BI displays this data alongside your marketing metrics, but it does not automatically optimize external marketing campaigns. It gives your team the necessary context to make better strategic decisions."
      },
      {
        q: "What kind of data does the Auto/Mate integration surface in Relevant BI?",
        a: "The integration can help surface insights related to dealership operations, fixed/service operations, parts, and F&I reporting, depending on your configured access. This data serves as operational context for evaluating your marketing and advertising reports."
      },
      {
        q: "Is Auto/Mate data a replacement for CRM or ad platform data?",
        a: "No. Auto/Mate is a dealership management software system. It complements your marketing data by providing operational context but does not replace the specialized metrics found in your CRM or advertising platforms."
      }
    ],
    relatedSlugs: ["fortellis", "marketcheck"]
  },
  {
    name: "CallRevu",
    slug: "callrevu",
    logo: "/images/relevant-bi-integrations/callrevu.png",
    website: "https://callrevu.com/",
    seoTitle: "Relevant BI Integration with CallRevu",
    seoDescription: "Discover the benefits of integrating Relevant BI with CallRevu. See your automotive communication and inbound call analytics alongside your marketing performance.",
    shortDescription: "Bring CallRevu's inbound call intelligence and analytics into Relevant BI to see how phone interactions align with marketing channels.",
    intro: "Relevant BI integration with CallRevu allows you to connect automotive communication intelligence directly into your marketing hub. CallRevu specializes in inbound calls and leads, call analytics, attribution context, and coaching. Integrating Relevant BI with CallRevu provides dealership decision-makers with a unified view of how advertising investments translate into actual phone conversations.",
    sections: [
      {
        heading: "Aligning Inbound Calls with Marketing Spend",
        content: "Phone calls remain one of the highest-intent actions a car shopper can take. While CallRevu tracks the quality and context of these calls, viewing this data in isolation limits its strategic value. By bringing CallRevu data into Relevant BI, you can observe how different marketing campaigns correlate with call volume and quality, providing a richer understanding of your marketing impact."
      },
      {
        heading: "Contextualizing Communication Intelligence",
        content: "It is important to note that while this integration provides deep visibility into call analytics and attribution context, it does not guarantee specific sales outcomes. The goal is to equip your marketing and sales leadership with accurate, centralized information to improve coaching and refine marketing channel investments.",
        list: [
          "Compare call volume trends against advertising traffic",
          "Centralize inbound lead metrics alongside website engagement",
          "Use call analytics to inform marketing adjustments"
        ]
      }
    ],
    faqs: [
      {
        q: "Will integrating Relevant BI with CallRevu guarantee an increase in vehicle sales?",
        a: "No. CallRevu provides valuable automotive communication intelligence and call analytics, but tracking this data does not guarantee sales outcomes. It provides the visibility needed to improve processes and align marketing efforts."
      },
      {
        q: "How does this integration help our marketing team?",
        a: "By viewing CallRevu's inbound call metrics within Relevant BI, your marketing team can see how specific campaigns and traffic sources correlate with phone inquiries, helping them allocate budget more effectively."
      },
      {
        q: "Can I manage CallRevu coaching directly from Relevant BI?",
        a: "Relevant BI acts as a reporting and intelligence hub to display call analytics and context. Specific coaching workflows and platform configurations are managed directly within the CallRevu system."
      }
    ],
    relatedSlugs: ["dialpad", "net2phone", "ga4"]
  },
  {
    name: "CarGurus",
    slug: "cargurus",
    logo: "/images/relevant-bi-integrations/cargurus.png",
    website: "https://cargurus.com/",
    seoTitle: "Relevant BI Integration with CarGurus",
    seoDescription: "Learn about the Relevant BI integration with CarGurus. Centralize marketplace listings, shopper demand, and vehicle research signals in one dashboard.",
    shortDescription: "View your CarGurus marketplace data alongside your dealership's internal website and advertising metrics in Relevant BI.",
    intro: "Relevant BI integration with CarGurus brings vital automotive shopping marketplace data into your executive dashboard. CarGurus provides listings, pricing and deal context, shopper demand, and vehicle research signals. Integrating Relevant BI with CarGurus helps dealership leaders evaluate how their inventory performs on a major third-party marketplace compared to their own website and paid campaigns.",
    sections: [
      {
        heading: "Comparing Marketplace Attention and Internal Traffic",
        content: "Understanding how vehicles perform requires looking at multiple touchpoints. The Relevant BI integration with CarGurus allows you to see third-party marketplace engagement alongside the traffic driven by your Google Ads or organic search efforts. This combined view helps identify which vehicles are receiving attention across the board and which might need a different promotional approach."
      },
      {
        heading: "Public Marketplace Context",
        content: "CarGurus provides public performance data, shopper demand signals, and listing context. It is important to distinguish this from your private, internal performance data. CarGurus data reflects public marketplace interaction, and does not imply access to private or non-public competitor performance data. Relevant BI centralizes these insights so your team can make informed pricing and merchandising decisions.",
        list: [
          "Monitor listing visibility and shopper demand signals",
          "Compare third-party engagement with your dealership's website traffic",
          "Identify inventory that requires additional marketing support"
        ]
      }
    ],
    faqs: [
      {
        q: "Does the CarGurus integration give me access to private competitor sales data?",
        a: "No. CarGurus provides public marketplace data, listing context, and general shopper demand signals. It does not provide access to private or non-public performance data of other dealerships."
      },
      {
        q: "Why should we view CarGurus data in Relevant BI instead of just using the CarGurus dashboard?",
        a: "Integrating Relevant BI with CarGurus allows you to compare your marketplace performance directly against your website analytics and ad campaigns in a single, unified view, reducing the need to jump between multiple disjointed reports."
      },
      {
        q: "Can Relevant BI automatically adjust my CarGurus pricing?",
        a: "No. Relevant BI is an intelligence and reporting hub designed for analysis and collaboration. Pricing adjustments and listing management must be executed within your inventory management tool or the CarGurus platform itself."
      }
    ],
    relatedSlugs: ["marketcheck", "google-ads"]
  },
  {
    name: "Dialpad",
    slug: "dialpad",
    logo: "/images/relevant-bi-integrations/dialpad.png",
    website: "https://www.dialpad.com/",
    seoTitle: "Relevant BI Integration with Dialpad",
    seoDescription: "Explore the Relevant BI integration with Dialpad. Bring your AI-powered business communications and interaction context into your marketing intelligence hub.",
    shortDescription: "Connect Dialpad's business communications data with Relevant BI to align voice interactions and team performance with marketing outcomes.",
    intro: "Relevant BI integration with Dialpad centralizes your communication metrics within your broader marketing intelligence strategy. Dialpad is an AI-powered business communications and contact-center platform that handles voice, transcripts, interaction logs, and team performance context. By integrating Relevant BI with Dialpad, dealerships can observe how marketing initiatives drive actual communication volume and interactions.",
    sections: [
      {
        heading: "Connecting Campaigns to Conversations",
        content: "Marketing generates interest, but communications platforms capture the interaction. Bringing Dialpad data into Relevant BI allows you to compare communication volume—such as inbound calls and contact center interactions—against the traffic driven by your advertising. This helps leadership teams assess whether an increase in ad spend is genuinely leading to an increase in team interactions."
      },
      {
        heading: "Understanding the Data",
        content: "While Dialpad provides interaction and team performance context, it does not provide autonomous dealership compliance or guarantee sales outcomes. When configured, the integration helps make available communication data visible beside marketing data so human leaders can ask better-informed questions.",
        list: [
          "View voice and interaction volume alongside marketing traffic",
          "Contextualize team performance metrics with campaign timelines",
          "Create a shared workspace for analyzing communication trends"
        ]
      }
    ],
    faqs: [
      {
        q: "Does the Dialpad integration guarantee better sales outcomes or autonomous compliance?",
        a: "No. Dialpad provides interaction logs, voice data, and team performance context. It does not claim autonomous dealership compliance or guarantee specific sales outcomes. It provides visibility so your team can make informed improvements."
      },
      {
        q: "How does Dialpad data enhance marketing reports in Relevant BI?",
        a: "By integrating Relevant BI with Dialpad, you can see if spikes in website traffic or ad spend correspond with increases in voice interactions and contact-center activity, validating the quality of your marketing leads."
      },
      {
        q: "Can I manage Dialpad settings from the Relevant BI dashboard?",
        a: "Relevant BI is a reporting and intelligence hub. While you can view interaction data and trends, platform configuration, user management, and communication settings must be handled within Dialpad."
      }
    ],
    relatedSlugs: ["callrevu", "net2phone"]
  },
  {
    name: "Fortellis",
    slug: "fortellis",
    logo: "/images/relevant-bi-integrations/fortellis.png",
    website: "https://fortellis.io/",
    seoTitle: "Relevant BI Integration with Fortellis",
    seoDescription: "Learn how integrating Relevant BI with Fortellis helps centralize automotive API data, bridging the gap between OEMs, software providers, and your marketing.",
    shortDescription: "Utilize the Fortellis automotive API platform within Relevant BI to access a wider range of software provider and OEM data.",
    intro: "Relevant BI integration with Fortellis offers dealership leaders an expanded view of their automotive technology ecosystem. Fortellis is an automotive API platform and marketplace that facilitates integrations among dealers, OEMs, and software providers. Integrating Relevant BI with Fortellis allows you to pull in specialized data streams that give further context to your marketing and sales performance.",
    sections: [
      {
        heading: "Expanding Data Accessibility",
        content: "The modern dealership relies on numerous software tools. Fortellis acts as a bridge for these tools. By bringing data facilitated by Fortellis into the Relevant BI hub, you can combine specific API-driven metrics with your standard web and advertising analytics. This provides a more comprehensive view of how different operational and marketing elements interact."
      },
      {
        heading: "A Platform, Not the Source of Record",
        content: "It is important to understand the role of Fortellis in this ecosystem. Fortellis is a marketplace and API facilitator, not the operating system of record for your dealership. When an appropriate connection and permissions are configured, Relevant BI can present available data streams made accessible through the Fortellis network as context for executive reports.",
        list: [
          "Access specialized software provider data alongside marketing metrics",
          "Leverage API-driven insights to contextualize dealership performance",
          "Unify fragmented data streams into a single intelligence hub"
        ]
      }
    ],
    faqs: [
      {
        q: "Is Fortellis the operating system of record for my dealership?",
        a: "No. Fortellis is an automotive API platform and marketplace that facilitates integrations. It is not presented or used as the operating system of record. Available data depends on the applications, permissions, and APIs authorized through the connection."
      },
      {
        q: "Why do we need Fortellis if we already integrate with our CRM or DMS?",
        a: "Fortellis may provide access to specific OEM data or third-party software provider metrics that are not natively available through standard integrations, offering a more complete picture when combined with Relevant BI."
      },
      {
        q: "Can I manage Fortellis API connections within Relevant BI?",
        a: "Relevant BI consumes the data provided by authorized Fortellis APIs for reporting purposes. Managing the actual API marketplace subscriptions and network permissions is done through your Fortellis account."
      }
    ],
    relatedSlugs: ["automate", "marketcheck"]
  },
  {
    name: "Google Analytics 4",
    slug: "ga4",
    logo: "/images/relevant-bi-integrations/ga4.png",
    website: "https://developers.google.com/analytics",
    seoTitle: "Relevant BI Integration with Google Analytics 4",
    seoDescription: "Integrate Relevant BI with Google Analytics 4 (GA4) to view website acquisition, engagement, and event data within your dealership command center.",
    shortDescription: "Bring GA4 website acquisition, engagement, and conversion events into Relevant BI for a unified view of your digital showroom.",
    intro: "Relevant BI integration with Google Analytics 4 (GA4) adds important context for understanding your digital showroom's performance. GA4 measures website and app acquisition, user engagement, configured events, and conversions. Integrating Relevant BI with GA4 helps dealership executives evaluate aggregate online performance without relying only on disconnected interfaces or spreadsheets.",
    sections: [
      {
        heading: "Connecting Traffic to Dealership Actions",
        content: "GA4 provides the essential data on how users interact with your site, such as viewing Vehicle Detail Pages (VDPs) or completing forms. When you bring this data into Relevant BI, you can view it directly alongside your Google Ads spend and inventory data. This makes it easier to spot trends—like high traffic but low engagement—and ask better questions about your marketing strategy."
      },
      {
        heading: "Understanding GA4's Scope",
        content: "While GA4 is powerful for measuring aggregated behavior, it does not identify individual people. We never imply that GA4 provides person-level Personally Identifiable Information (PII) identification or complete, closed-loop sales attribution on its own. It is a measurement tool for acquisition and events, serving as a critical piece of a larger intelligence puzzle.",
        list: [
          "Monitor website traffic, acquisition sources, and engagement rates",
          "Track key events like VDP views and form submissions",
          "Compare web performance against active advertising campaigns"
        ]
      }
    ],
    faqs: [
      {
        q: "Does the GA4 integration allow me to see the exact names of people visiting my website?",
        a: "No. Google Analytics 4 measures aggregated website and app acquisition, engagement, and events. It does not provide person-level PII (Personally Identifiable Information) identification."
      },
      {
        q: "Will GA4 provide complete sales attribution for my dealership?",
        a: "No. GA4 tracks online conversions and events, but it does not provide complete sales attribution on its own. It must be viewed alongside CRM, DMS, and lead data to understand the full customer journey."
      },
      {
        q: "Why use Relevant BI instead of logging directly into GA4?",
        a: "Relevant BI simplifies complex GA4 data and displays it alongside your inventory, ads, and communication metrics. This provides dealership leaders with a clear, unified narrative rather than disconnected data points."
      }
    ],
    relatedSlugs: ["google-ads", "google-search-console", "google-cloud"]
  },
  {
    name: "Google Ads",
    slug: "google-ads",
    logo: "/images/relevant-bi-integrations/google-ads.png",
    website: "https://developers.google.com/ads",
    seoTitle: "Relevant BI Integration with Google Ads",
    seoDescription: "Learn about the Relevant BI integration with Google Ads. Bring campaign, click, spend, and conversion measurement into your unified dealership dashboard.",
    shortDescription: "View your Google Ads campaign spend, clicks, and conversion measurement alongside website and inventory data in Relevant BI.",
    intro: "Relevant BI integration with Google Ads provides essential visibility into your paid search and display investments. Google Ads supplies data on campaigns, clicks, spend, and conversion measurement. By integrating Relevant BI with Google Ads, dealership leaders can hold their marketing investments accountable by viewing paid performance next to actual website engagement and inventory interest.",
    sections: [
      {
        heading: "Measuring Paid Performance in Context",
        content: "Advertising data is most useful when compared with other enabled signals. Bringing Google Ads metrics into Relevant BI can help teams compare high-spend campaigns with configured website events, VDP activity, or call data rather than evaluating clicks alone. That context can reveal areas that deserve closer budget review."
      },
      {
        heading: "Ad Data vs. Dealership Data",
        content: "It is crucial to note that ad-platform data is not a substitute for internal lead, sales, or DMS data. Google Ads measures what happens on the advertising network and the immediate actions taken on your site. Relevant BI brings this ad data into the same workspace as your other tools, allowing you to bridge the gap between a reported 'conversion' and a realized business outcome.",
        list: [
          "Track campaign spend, clicks, and cost-per-conversion",
          "Compare Google Ads traffic quality against organic and direct sources",
          "Evaluate advertising efficiency using a shared executive view"
        ]
      }
    ],
    faqs: [
      {
        q: "Is Google Ads data a replacement for my CRM sales data?",
        a: "No. Ad-platform data measures campaign clicks, spend, and digital conversions. It is not a substitute for internal lead, sales, or DMS data, which track actual vehicles sold and revenue generated."
      },
      {
        q: "What specific Google Ads metrics will I see in Relevant BI?",
        a: "The integration typically surfaces campaign performance, click volume, total spend, and platform conversion measurement, allowing you to gauge the immediate impact of your paid advertising."
      },
      {
        q: "Can I pause or edit my Google Ads campaigns directly from Relevant BI?",
        a: "Relevant BI is a reporting and intelligence hub designed to help you ask better questions and collaborate. Campaign execution, such as adjusting bids or pausing ads, must be done within the Google Ads platform."
      }
    ],
    relatedSlugs: ["ga4", "google-search-console"]
  },
  {
    name: "Google Cloud",
    slug: "google-cloud",
    logo: "/images/relevant-bi-integrations/google-cloud.png",
    website: "https://cloud.google.com/",
    seoTitle: "Relevant BI Integration with Google Cloud",
    seoDescription: "Discover the Relevant BI integration with Google Cloud. Utilize robust cloud infrastructure and analytics services for deeper dealership intelligence.",
    shortDescription: "Leverage Google Cloud data and analytics services within Relevant BI to process and view connected datasets efficiently.",
    intro: "Relevant BI integration with Google Cloud can support dealerships that maintain enabled datasets in cloud infrastructure. Google Cloud provides infrastructure, storage, data, and analytics services. With the appropriate connection and permissions, Relevant BI can display selected Google Cloud-hosted data beside other marketing intelligence.",
    sections: [
      {
        heading: "Handling Complex Datasets",
        content: "As dealerships generate more data across multiple rooftops and systems, robust infrastructure is required to make sense of it all. By integrating connected datasets hosted on Google Cloud into Relevant BI, leadership teams can access high-level reporting without worrying about the underlying data pipelines. This brings advanced data analytics services directly into an accessible, executive-friendly interface."
      },
      {
        heading: "Infrastructure, Not Automation",
        content: "Google Cloud provides the necessary infrastructure and data services to power complex reporting. It is important to clarify that this integration describes connected and enabled datasets for reporting purposes; it does not provide automatic dealership optimization. The value lies in giving your team the clear information they need to make strategic human decisions.",
        list: [
          "Connect enabled datasets hosted on Google Cloud infrastructure",
          "Utilize advanced analytics services for executive reporting",
          "Maintain a stable, secure flow of intelligence into Relevant BI"
        ]
      }
    ],
    faqs: [
      {
        q: "Does the Google Cloud integration automatically optimize my dealership operations?",
        a: "No. Google Cloud provides infrastructure, storage, and data analytics services. The integration connects these enabled datasets to Relevant BI for reporting and analysis, but it does not automatically optimize operations or marketing."
      },
      {
        q: "What type of data does this integration handle?",
        a: "This integration is tailored for dealerships that utilize Google Cloud for custom data storage or analytics services, allowing those specific connected datasets to be visualized within the Relevant BI hub."
      },
      {
        q: "Is Google Cloud required to use Relevant BI?",
        a: "No. Relevant BI connects to many individual platforms directly. The Google Cloud integration is specifically for dealerships that already use its infrastructure for their own data aggregation or advanced analytics."
      }
    ],
    relatedSlugs: ["ga4", "google-ads"]
  },
  {
    name: "Google Search Console",
    slug: "google-search-console",
    logo: "/images/relevant-bi-integrations/google-search-console.png",
    website: "https://search.google.com/search-console/amp",
    seoTitle: "Relevant BI Integration with Google Search Console",
    seoDescription: "Integrate Relevant BI with Google Search Console to monitor organic search visibility, queries, and impressions directly from your dealership dashboard.",
    shortDescription: "View organic search visibility, queries, clicks, and impressions from Google Search Console alongside your other marketing data.",
    intro: "Relevant BI integration with Google Search Console is critical for understanding your dealership's organic visibility. Google Search Console supplies organic search visibility data such as queries, pages, clicks, impressions, position, and indexing context. Integrating Relevant BI with Google Search Console allows you to monitor how well your website performs in free search results without needing a separate SEO tool.",
    sections: [
      {
        heading: "Monitoring Organic Health",
        content: "Organic search is a primary driver of high-quality traffic for dealerships. By bringing Search Console data into Relevant BI, you can easily track which search queries are driving users to your inventory pages and service departments. When viewed next to GA4 engagement data, you can assess not only if people are finding you, but if they are staying on your site."
      },
      {
        heading: "Strictly Organic Insights",
        content: "Google Search Console focuses entirely on organic search performance. It does not cover paid advertising campaigns (which are handled by Google Ads) or track final dealership sales. By separating these data sources visually but keeping them in the same hub, Relevant BI helps you understand the distinct value of your organic SEO efforts versus your paid investments.",
        list: [
          "Track organic search impressions, clicks, and average positions",
          "Identify which queries drive traffic to specific vehicle pages",
          "Monitor website indexing context alongside engagement metrics"
        ]
      }
    ],
    faqs: [
      {
        q: "Does Google Search Console data include my paid search campaigns?",
        a: "No. Google Search Console supplies data exclusively on organic search visibility. Paid advertising data is managed through Google Ads."
      },
      {
        q: "Can this integration tell me which organic search clicks resulted in a car sale?",
        a: "No. Search Console provides data on search queries, clicks, and impressions. It does not track user behavior after they click through to your site, nor does it track dealership sales."
      },
      {
        q: "How does Search Console data differ from GA4 data in Relevant BI?",
        a: "Search Console shows how your site performs in Google search results (impressions, queries, click-through rates). GA4 shows what users do once they actually arrive on your website (page views, time on site, events)."
      }
    ],
    relatedSlugs: ["ga4", "google-ads"]
  },
  {
    name: "MarketCheck",
    slug: "marketcheck",
    logo: "/images/relevant-bi-integrations/marketcheck.png",
    website: "https://www.marketcheck.com/",
    seoTitle: "Relevant BI Integration with MarketCheck",
    seoDescription: "Learn about integrating Relevant BI with MarketCheck. Bring automotive inventory, pricing history, and market data APIs into your intelligence hub.",
    shortDescription: "Incorporate MarketCheck's automotive inventory and pricing history data into Relevant BI for broader public market context.",
    intro: "Relevant BI integration with MarketCheck adds crucial public automotive data to your intelligence hub. MarketCheck provides automotive inventory, VIN/build data, listings, pricing history, and broad market data via APIs, feeds, and reports. By integrating Relevant BI with MarketCheck, dealership leaders can compare their own inventory performance and pricing strategies against wider public market trends.",
    sections: [
      {
        heading: "Understanding the Broader Market",
        content: "To make informed marketing and pricing decisions, dealerships need to look beyond their own lot. By bringing MarketCheck data into the Relevant BI dashboard, you can view market-wide pricing history and listing data alongside your website traffic and ad spend. This helps answer questions like whether a specific model is receiving low engagement due to marketing efforts or simply because it is priced above current market trends."
      },
      {
        heading: "Public Data vs. Private Records",
        content: "MarketCheck provides an invaluable view of the public automotive market. However, it is essential to remember that this public-market data is not a substitute for, nor does it equate to, your private DMS records. MarketCheck helps you understand the external environment, while your DMS and internal tools track your actual operational reality.",
        list: [
          "Access public automotive inventory and pricing history",
          "Compare your website engagement against broader market trends",
          "Utilize VIN and build data to contextualize marketing performance"
        ]
      }
    ],
    faqs: [
      {
        q: "Does MarketCheck provide data from other dealerships' private DMS records?",
        a: "No. MarketCheck provides data aggregated from public listings, pricing history, and market feeds. It does not provide access to, nor should it be equated with, private DMS records."
      },
      {
        q: "How does MarketCheck data help my marketing strategy?",
        a: "By viewing public market data alongside your marketing metrics in Relevant BI, you can determine if a vehicle's low performance is due to ineffective advertising or if it reflects a broader market trend in pricing or demand."
      },
      {
        q: "Can I manage my own inventory through the MarketCheck integration in Relevant BI?",
        a: "Relevant BI uses MarketCheck for reporting and market intelligence context. Active management of your inventory must be handled through your inventory syndication tools or DMS."
      }
    ],
    relatedSlugs: ["cargurus", "automate", "fortellis"]
  },
  {
    name: "net2phone",
    slug: "net2phone",
    logo: "/images/relevant-bi-integrations/net2phone.png",
    website: "https://www.net2phone.com/",
    seoTitle: "Relevant BI Integration with net2phone",
    seoDescription: "Explore integrating Relevant BI with net2phone to view unified communications, voice, and performance analytics alongside your dealership marketing data.",
    shortDescription: "Connect net2phone's unified communications and interaction logs to Relevant BI to analyze phone performance alongside marketing traffic.",
    intro: "Relevant BI integration with net2phone can place available dealership communication data closer to the marketing decisions it informs. net2phone provides unified communications, contact-center voice, messaging, interaction logs, and performance analytics. With configured access, dealership leaders can compare communication trends with campaign and website activity.",
    sections: [
      {
        heading: "Aligning Voice Data with Digital Metrics",
        content: "A successful marketing campaign often results in a phone call rather than a form submission. By bringing net2phone interaction logs and voice analytics into the Relevant BI hub, you can directly compare communication volumes against your active Google Ads or organic search traffic. This unified view helps validate the quality of the leads your marketing efforts are generating."
      },
      {
        heading: "Analytics for Human Improvement",
        content: "While net2phone offers detailed performance analytics and messaging logs, this integration does not guarantee the elimination of human error or specific sales results. The purpose of bringing this data into Relevant BI is to provide management with clear, objective data to improve team coaching, adjust marketing spend, and better understand the customer journey.",
        list: [
          "View contact-center voice and messaging volumes in one dashboard",
          "Correlate communication interaction logs with marketing campaigns",
          "Provide leadership with unified performance analytics for coaching"
        ]
      }
    ],
    faqs: [
      {
        q: "Will the net2phone integration automatically improve my team's sales results?",
        a: "No. net2phone provides unified communications data and performance analytics. Having this data in Relevant BI helps you identify areas for improvement, but it does not guarantee sales results or eliminate human error."
      },
      {
        q: "What net2phone data is visible in Relevant BI?",
        a: "The integration typically surfaces interaction logs, overall voice and messaging volume, and general performance analytics, allowing you to compare communication trends with marketing traffic."
      },
      {
        q: "Do I use Relevant BI to answer calls or send messages?",
        a: "No. Relevant BI is purely a reporting and intelligence platform. All active communications, call routing, and messaging are handled directly within the net2phone system."
      }
    ],
    relatedSlugs: ["callrevu", "dialpad"]
  }
];

export const integrations: Integration[] = [...integrationData].sort((a, b) =>
  a.name.localeCompare(b.name, "en-US", { sensitivity: "base" }),
);

export function getIntegrationBySlug(slug: string): Integration | undefined {
  return integrations.find((i) => i.slug === slug);
}
